import { SSO_LOGOUT_URL } from 'constants/Constant'
import axios from '../utils/axios'

/**
 * SSO Service - Handles Single Sign-On operations
 * Provides modular functions for SSO logout and session management
 */
const SSOService = {}

/**
 * Makes an axios request to SSO logout endpoint
 * @param {string} url - The SSO logout URL
 * @returns {Promise} Promise that resolves regardless of success/failure
 */
SSOService.callLogoutAPI = async (url = SSO_LOGOUT_URL) => {
  try {
    console.log('Calling SSO logout API:', url)

    // Create a new axios instance without authentication for SSO logout
    const ssoAxios = axios.create({
      timeout: 5000,
      withCredentials: true, // Include cookies for SSO logout
    })

    await ssoAxios.get(url)
    console.log('SSO logout API call successful')
  } catch (error) {
    // Expected to fail due to CORS, but the request still triggers SSO logout
    console.warn('SSO logout API call failed (expected due to CORS):', error.message)
  }
}

/**
 * Clears local browser storage and cookies related to SSO
 */
SSOService.clearLocalSessionData = () => {
  try {
    console.log('Clearing local session data')

    // Clear localStorage items that might be related to SSO
    const ssoKeys = Object.keys(localStorage).filter(
      (key) =>
        key.toLowerCase().includes('sso') ||
        key.toLowerCase().includes('auth') ||
        key.toLowerCase().includes('token') ||
        key.toLowerCase().includes('session')
    )

    ssoKeys.forEach((key) => {
      localStorage.removeItem(key)
      console.log(`Cleared localStorage key: ${key}`)
    })

    // Clear sessionStorage items that might be related to SSO
    const sessionSsoKeys = Object.keys(sessionStorage).filter(
      (key) =>
        key.toLowerCase().includes('sso') ||
        key.toLowerCase().includes('auth') ||
        key.toLowerCase().includes('token') ||
        key.toLowerCase().includes('session')
    )

    sessionSsoKeys.forEach((key) => {
      sessionStorage.removeItem(key)
      console.log(`Cleared sessionStorage key: ${key}`)
    })

    console.log('Local session data cleared successfully')
  } catch (error) {
    console.warn('Failed to clear some local session data:', error)
  }
}

/**
 * Comprehensive SSO logout function
 * Combines iframe approach, API call, and local storage cleanup
 * @returns {Promise} Promise that resolves when all logout steps are complete
 */
SSOService.logout = async () => {
  console.log('Initiating SSO logout')

  try {

    // Wait for API call to complete
    await SSOService.callLogoutAPI()

    // Clear local data after remote logout attempts
    SSOService.clearLocalSessionData()

    console.log('SSO logout completed successfully')
  } catch (error) {
    console.error('Error during SSO logout:', error)
    throw error
  }
}

export default SSOService
